import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api/client';

const BASE_TABS = ['overview', 'oracle', 'assistant', 'play', 'characters', 'heat', 'npcs', 'locations', 'organizations', 'plots', 'sessions', 'calendar', 'wiki', 'future', 'graph', 'data'];

export function useFeatureFlags(isAuthenticated: boolean, activeCampaignId: number | null) {
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [featureFlagsCampaignId, setFeatureFlagsCampaignId] = useState<number | null>(null);

  const isFlagActive = useCallback(
    (key: string) =>
      activeCampaignId != null &&
      featureFlagsCampaignId === activeCampaignId &&
      Boolean(featureFlags[key]),
    [activeCampaignId, featureFlagsCampaignId, featureFlags]
  );

  const mapsEnabled = isFlagActive('maps');
  const timelinesEnabled = isFlagActive('timelines');
  const generatorsEnabled = isFlagActive('generators');
  const familyTreesEnabled = isFlagActive('family_trees');
  const diplomacyWebsEnabled = isFlagActive('diplomacy_webs');
  const whiteboardsEnabled = isFlagActive('whiteboards');
  const contentTreesEnabled = isFlagActive('content_trees');
  const campaignRunEnabled = isFlagActive('campaign_run');
  const statblocksEnabled = isFlagActive('statblocks');
  const rulesCompendiumEnabled = isFlagActive('rules_compendium');
  const characterSheetsEnabled = isFlagActive('character_sheets');
  const encounterBuilderEnabled = isFlagActive('encounter_builder');
  const publishingEnabled = isFlagActive('publishing');
  const assistantMemoryEnabled = isFlagActive('assistant_memory');
  const integrationsEnabled = isFlagActive('integrations');
  const analyticsEnabled = isFlagActive('analytics');

  const refreshFeatureFlags = useCallback(async () => {
    if (!isAuthenticated || activeCampaignId == null) return;
    try {
      const flags = await api.getFeatureFlags();
      const nextFlags: Record<string, boolean> = {};
      for (const flag of flags) {
        nextFlags[flag.key] = flag.enabled;
      }
      setFeatureFlagsCampaignId(activeCampaignId);
      setFeatureFlags(nextFlags);
    } catch {
      setFeatureFlagsCampaignId(activeCampaignId);
      setFeatureFlags({});
    }
  }, [activeCampaignId, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || activeCampaignId == null) return;
    let isActive = true;
    const loadFeatureFlags = async () => {
      try {
        const flags = await api.getFeatureFlags();
        if (!isActive) return;
        const next: Record<string, boolean> = {};
        for (const flag of flags) {
          next[flag.key] = flag.enabled;
        }
        setFeatureFlagsCampaignId(activeCampaignId);
        setFeatureFlags(next);
      } catch {
        if (!isActive) return;
        setFeatureFlagsCampaignId(activeCampaignId);
        setFeatureFlags({});
      }
    };
    void loadFeatureFlags();
    return () => {
      isActive = false;
    };
  }, [activeCampaignId, isAuthenticated]);

  const tabs = useMemo(() => {
    const orderedTabs = [...BASE_TABS];
    const calendarIndex = orderedTabs.indexOf('calendar');
    let nextCalendarInsertIndex = calendarIndex >= 0 ? calendarIndex + 1 : orderedTabs.length;
    const insertBeforeData = (tabKey: string) => {
      if (orderedTabs.includes(tabKey)) return;
      const dataIndex = orderedTabs.indexOf('data');
      if (dataIndex >= 0) {
        orderedTabs.splice(dataIndex, 0, tabKey);
      } else {
        orderedTabs.push(tabKey);
      }
    };

    if (mapsEnabled) {
      orderedTabs.splice(nextCalendarInsertIndex, 0, 'maps');
      nextCalendarInsertIndex += 1;
    }
    if (timelinesEnabled) {
      orderedTabs.splice(nextCalendarInsertIndex, 0, 'timelines');
      nextCalendarInsertIndex += 1;
    }
    if (generatorsEnabled || familyTreesEnabled || diplomacyWebsEnabled) {
      insertBeforeData('generators');
    }
    if (whiteboardsEnabled) {
      insertBeforeData('whiteboards');
    }
    if (contentTreesEnabled) {
      insertBeforeData('content-trees');
    }
    if (rulesCompendiumEnabled) {
      insertBeforeData('rules');
    }
    if (characterSheetsEnabled) {
      insertBeforeData('sheets');
    }
    if (encounterBuilderEnabled) {
      insertBeforeData('encounters');
    }
    if (publishingEnabled) {
      insertBeforeData('publish');
    }
    if (assistantMemoryEnabled) {
      insertBeforeData('memory');
    }
    if (integrationsEnabled) {
      insertBeforeData('integrations');
    }
    if (analyticsEnabled) {
      insertBeforeData('analytics');
    }
    return orderedTabs;
  }, [
    assistantMemoryEnabled,
    characterSheetsEnabled,
    contentTreesEnabled,
    diplomacyWebsEnabled,
    encounterBuilderEnabled,
    familyTreesEnabled,
    generatorsEnabled,
    analyticsEnabled,
    integrationsEnabled,
    mapsEnabled,
    publishingEnabled,
    rulesCompendiumEnabled,
    timelinesEnabled,
    whiteboardsEnabled,
  ]);

  return {
    featureFlags,
    refreshFeatureFlags,
    tabs,
    mapsEnabled,
    timelinesEnabled,
    generatorsEnabled,
    familyTreesEnabled,
    diplomacyWebsEnabled,
    whiteboardsEnabled,
    contentTreesEnabled,
    campaignRunEnabled,
    statblocksEnabled,
    rulesCompendiumEnabled,
    characterSheetsEnabled,
    encounterBuilderEnabled,
    publishingEnabled,
    assistantMemoryEnabled,
    integrationsEnabled,
    analyticsEnabled,
  };
}
